<template>
  <div class="staff-picker">
    <el-input
      v-if="hasOwnSearch"
      ref="searchInput"
      v-model="ownQuery"
      placeholder="Search staff by name"
      clearable
      :prefix-icon="Search"
      @keydown.enter.prevent="chooseHighlighted"
      @keydown.down.prevent="moveHighlight(1)"
      @keydown.up.prevent="moveHighlight(-1)"
    />

    <ul ref="listEl" class="staff-picker__list">
      <template v-if="!query.trim()">
        <li v-if="unassignable">
          <button type="button" class="staff-picker__option" @click="choose(null)">
            <UserRoundX :size="16" class="staff-picker__icon" /> Unassign
          </button>
        </li>
        <li v-if="currentStaffId && assignableToMe">
          <button type="button" class="staff-picker__option" @click="choose(currentStaffId)">
            <UserRoundCheck :size="16" class="staff-picker__icon" /> Assign to me
          </button>
        </li>
      </template>

      <template v-for="section in sections" :key="section.label">
        <li class="staff-picker__section">{{ section.label }}</li>
        <li v-for="person in section.staff" :key="person.id">
          <button
            type="button"
            class="staff-picker__option"
            :class="{ 'is-highlighted': person.id === highlightedId }"
            @mouseenter="highlightedId = person.id"
            @click="choose(person.id)"
          >
            <StaffInitialsAvatar :full-name="person.fullName" small />
            {{ person.displayName }}
            <span v-if="markViewer && person.id === currentStaffId" class="staff-picker__you">(you)</span>
          </button>
        </li>
      </template>

      <li v-if="loading" class="staff-picker__hint"><i class="fas fa-spinner fa-spin tw-mr-1" /> Loading staff…</li>
      <li v-else-if="!matches.length" class="staff-picker__hint">
        {{ query.trim() ? `No staff match “${query.trim()}”` : "No staff found" }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { Search } from "@element-plus/icons-vue"
import { UserRoundCheck, UserRoundX } from "lucide-vue-next"
import StaffInitialsAvatar from "@/modules/patient-conversations/components/StaffInitialsAvatar.vue"
import {
  filterStaffByName,
  PatientConversationStaffOption
} from "@/modules/patient-conversations/composables/staff-directory"
import { STAFF_PICKER_SECTIONS } from "@/modules/patient-conversations/constants/patient-conversation-labels"
import { useCurrentStaffId } from "@/modules/patient-conversations/composables/current-staff"

// The searchable staff list shared by the inbox "Assign" control, the New message assignee, the thread's Reassign
// and the composer's "@" picker
const props = withDefaults(
  defineProps<{
    staff: ReadonlyArray<PatientConversationStaffOption>
    loading?: boolean
    /** Offers "Unassign" as the first option. */
    unassignable?: boolean
    /** Offers "Assign to me"; off when everything selected is already the viewer's. */
    assignableToMe?: boolean
    /** Filters by this text instead of showing a search box (the composer passes the text typed after "@"). */
    externalQuery?: string
    /** Labels the viewer "(you)" in the sections. */
    markViewer?: boolean
  }>(),
  { loading: false, unassignable: false, assignableToMe: true, externalQuery: undefined, markViewer: false }
)

const emit = defineEmits<{
  /** The chosen assignee's `usr_…` id, or `null` for "Unassign". */
  (e: "select", staffId: string | null): void
}>()

const currentStaffId = useCurrentStaffId()
const ownQuery = ref<string>("")
const searchInput = ref<{ focus: () => void }>()
const listEl = ref<HTMLElement>()

const hasOwnSearch = computed<boolean>(() => props.externalQuery === undefined)
const query = computed<string>(() => props.externalQuery ?? ownQuery.value)

const matches = computed<ReadonlyArray<PatientConversationStaffOption>>(() =>
  filterStaffByName(props.staff, query.value)
)

const sections = computed(() =>
  STAFF_PICKER_SECTIONS.map((section) => ({
    label: section.label,
    staff: matches.value.filter((person) => person.role === section.role)
  })).filter((section) => section.staff.length > 0)
)

// Keyboard navigation runs over the sections in display order. The first match is pre-highlighted once there is
// a query (or always for the "@" picker); the plain assignee list starts with nothing highlighted
const orderedMatches = computed<Array<PatientConversationStaffOption>>(() =>
  sections.value.flatMap((section) => section.staff)
)
const highlightedId = ref<string | undefined>()
watch(
  orderedMatches,
  (options) => {
    highlightedId.value = query.value.trim() || !hasOwnSearch.value ? options[0]?.id : undefined
  },
  { immediate: true }
)

function choose(staffId: string | null): void {
  emit("select", staffId)
}

async function moveHighlight(delta: number): Promise<void> {
  const options = orderedMatches.value
  if (!options.length) return
  const current = options.findIndex((person) => person.id === highlightedId.value)
  // With nothing highlighted, ↓ starts at the first option and ↑ at the last
  const from = current === -1 && delta < 0 ? 0 : current
  highlightedId.value = options[(from + delta + options.length) % options.length]?.id
  await nextTick()
  listEl.value?.querySelector(".is-highlighted")?.scrollIntoView({ block: "nearest" })
}

function chooseHighlighted(): void {
  const highlighted =
    orderedMatches.value.find((person) => person.id === highlightedId.value) ?? orderedMatches.value[0]
  if (highlighted) choose(highlighted.id)
}

function focus(): void {
  searchInput.value?.focus()
}

function reset(): void {
  ownQuery.value = ""
}

defineExpose({ focus, reset, moveHighlight, chooseHighlighted })
</script>

<style scoped lang="sass">
@import "@/styles/colors"

.staff-picker
  display: flex
  flex-direction: column
  gap: 6px

  &__list
    list-style: none
    margin: 0
    padding: 0
    max-height: 320px
    overflow-y: auto

  &__option
    display: flex
    align-items: center
    gap: 8px
    width: 100%
    padding: 7px 10px
    border: 0
    border-radius: 6px
    background: transparent
    color: $empty-text
    font-size: 14px
    text-align: left
    cursor: pointer

    &:hover, &:focus-visible, &.is-highlighted
      background-color: $vitable-green-background

  &__icon
    flex: none
    color: $grey

  &__you
    font-size: 12px
    color: $grey

  &__section
    padding: 10px 10px 4px
    font-size: 11px
    font-weight: 700
    letter-spacing: 0.06em
    color: $grey

  &__hint
    padding: 8px 10px
    font-size: 12px
    color: $grey
</style>
